import { isPaidEvent } from "@circle-react/components/EventsV3/Modal/utils";
import { BlockInViewOnlyMode } from "@circle-react/components/ViewOnlyMasquerading/BlockInViewOnlyMode";
import type { Event } from "@circle-react/types/Event";
import { EVENT_TYPES, RSVP_STATUS } from "../../helpers/constants";
import { ConfirmationModal } from "../ConfirmationModal";
import { UnrsvpConfirmationModal } from "../UnrsvpConfirmationModal";
import { useEventRsvp } from "../hooks/useEventRsvp";
import { RsvpButton } from "./RsvpButton";
import { RsvpRadio } from "./RsvpRadio";

interface RsvpActionProps {
  detailsPage?: boolean;
  event: Event;
  full: boolean;
  onChange: (event: Event) => void;
  toggleRecurringRsvp: () => void;
  variant?: "primary" | "secondary";
}

export const RsvpAction = ({
  event,
  onChange,
  toggleRecurringRsvp,
  full,
  variant = "primary",
  detailsPage = false,
}: RsvpActionProps) => {
  const {
    toggleRsvp,
    declineInvite,
    isLoading,
    shouldShowConfirmation,
    dismissConfirmation,
    dismissUnrsvpConfirmation,
    hasEventEnded,
    isConfirmed,
    rsvpStatus,
    setShouldShowUnrsvpConfirmation,
    shouldShowUnrsvpConfirmation,
  } = useEventRsvp({ event, onChange });

  const { event_type, invited_attendee: isAttendeeAccessTypeInvited } = event;
  const isRecurring = event_type === EVENT_TYPES.RECURRING;
  const {
    event_setting_attributes: eventData,
    paywall_attributes: ticketData,
  } = event || {};
  const { rsvp_limit_reached: isRsvpLimitReached } = eventData || {};
  const isFreeRecurringEvent = isRecurring && !isPaidEvent(event);
  const isAttendeeInvited = rsvpStatus === RSVP_STATUS.INVITED;
  const shouldShowRecurringRsvp =
    isFreeRecurringEvent && !isAttendeeAccessTypeInvited;
  const commonProps = {
    isConfirmed,
    rsvpStatus,
    full,
    toggleRsvp: shouldShowRecurringRsvp ? toggleRecurringRsvp : toggleRsvp,
    declineInvite,
    isLoading,
    paidEvent: isPaidEvent(event),
    setShouldShowUnrsvpConfirmation,
    showUnrsvpPopup: isPaidEvent(event) && !ticketData?.invited,
  };

  if (hasEventEnded) {
    return null;
  }

  const isRsvpActionNeeded = !rsvpStatus || isAttendeeInvited;

  const shouldShowConfirmationModal =
    isConfirmed && (!isRecurring || !shouldShowRecurringRsvp);

  return (
    <BlockInViewOnlyMode>
      {!isRsvpActionNeeded ? (
        <RsvpRadio {...commonProps} />
      ) : (
        <RsvpButton
          {...commonProps}
          variant={variant}
          isRsvpDisabled={isRsvpLimitReached}
          detailsPage={detailsPage}
        />
      )}
      {isConfirmed && isPaidEvent(event) && (
        <UnrsvpConfirmationModal
          isOpen={shouldShowUnrsvpConfirmation}
          onClose={dismissUnrsvpConfirmation}
          toggleRsvp={toggleRsvp}
        />
      )}
      {shouldShowConfirmationModal && (
        <ConfirmationModal
          event={event}
          show={shouldShowConfirmation}
          onClose={dismissConfirmation}
        />
      )}
    </BlockInViewOnlyMode>
  );
};
